// Compact, presentation-only live caps and input-readiness projections. The server supplies booleans and
// counts only; this component cannot render account, mailbox, portal, target or provider identifiers.

import type {
  JourneyLiveBudgetCapsView,
  JourneyLiveInputReadinessView,
} from '../journey-live-api.ts';

function penceLabel(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

export function JourneyLiveBudgetCaps({
  budgets,
}: {
  readonly budgets: JourneyLiveBudgetCapsView;
}): React.JSX.Element {
  const items = [
    { label: 'Model / run', value: penceLabel(budgets.maxModelRunPence) },
    {
      label: 'Journey / pack',
      value: `${penceLabel(budgets.maxJourneyPence)} / ${penceLabel(budgets.maxPackPence)}`,
    },
    {
      label: 'Email sends / reads / replies',
      value: `${budgets.maxEmailSends} / ${budgets.maxMailboxReads} / ${budgets.maxMailboxReplies}`,
    },
    {
      label: 'HubSpot reads / writes',
      value: `${budgets.maxHubspotReads} / ${budgets.maxHubspotWrites}`,
    },
    {
      label: 'Journey / step wait',
      value: `${budgets.maxJourneyDurationSeconds}s / ${budgets.maxStepWaitSeconds}s`,
    },
    { label: 'Read retries', value: String(budgets.maxReadRetries) },
  ] as const;
  return (
    <section aria-labelledby="journey-live-budgets" className="bg-surface-sunken p-4">
      <h3 id="journey-live-budgets" className="text-sm font-semibold text-text">
        Hard budget caps
      </h3>
      <dl aria-label="Live budget caps" className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
        {items.map((item) => (
          <div key={item.label}>
            <dt className="text-text-subtle">{item.label}</dt>
            <dd className="ritona-numeric mt-1 text-text-muted">{item.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

interface ReadinessRow {
  readonly code: string;
  readonly label: string;
  readonly ready: boolean;
  readonly readyLabel?: string;
  readonly blockedLabel?: string;
}

function namedReadinessRows(readiness: JourneyLiveInputReadinessView): readonly ReadinessRow[] {
  return [
    { code: 'manifest', label: 'Reviewed manifest', ready: readiness.manifestLoaded },
    { code: 'bindings', label: 'Private bindings', ready: readiness.privateBindingsLoaded },
    {
      code: 'binding-mode',
      label: 'Binding file mode',
      ready: readiness.privateBindingsOwnerOnly,
      readyLabel: 'Owner-only',
      blockedLabel: 'Unsafe',
    },
    {
      code: 'operator',
      label: 'Deployment operator',
      ready: readiness.deploymentOperatorCredentialPresent,
    },
    { code: 'model', label: 'Model credential', ready: readiness.modelCredentialPresent },
    { code: 'mailbox', label: 'Mailbox credential', ready: readiness.mailboxCredentialPresent },
    {
      code: 'mail-transport',
      label: 'Mail transport',
      ready: readiness.mailTransportCredentialsPresent,
    },
    {
      code: 'hubspot-observer',
      label: 'HubSpot observer',
      ready: readiness.hubspotObserverCredentialPresent,
    },
    {
      code: 'hubspot-read-only',
      label: 'HubSpot posture',
      ready: readiness.hubspotObserverReadOnly,
      readyLabel: 'Read-only',
      blockedLabel: 'Not read-only',
    },
    {
      code: 'evidence-store',
      label: 'Evidence store',
      ready: readiness.evidenceStoreIsolated,
      readyLabel: 'Isolated',
      blockedLabel: 'Not isolated',
    },
    { code: 'attestation', label: 'Target attestation', ready: readiness.attestationAdapterReady },
    { code: 'runner', label: 'Provider-live runner', ready: readiness.liveRunnerReady },
  ];
}

function ReadinessItem({ row }: { readonly row: ReadinessRow }): React.JSX.Element {
  return (
    <li
      className="flex items-center justify-between gap-3 border-b border-border pb-1"
      data-live-readiness={row.code}
      data-live-ready={String(row.ready)}
    >
      <span className="text-text-muted">{row.label}</span>
      <span className={row.ready ? 'text-forest' : 'text-clay'}>
        {row.ready ? (row.readyLabel ?? 'Ready') : (row.blockedLabel ?? 'Missing')}
      </span>
    </li>
  );
}

export function JourneyLiveInputReadiness({
  readiness,
}: {
  readonly readiness: JourneyLiveInputReadinessView;
}): React.JSX.Element {
  return (
    <section aria-labelledby="journey-live-readiness" className="bg-surface-sunken p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 id="journey-live-readiness" className="text-sm font-semibold text-text">
          Input readiness
        </h3>
        <span className="ritona-numeric text-xs text-text-subtle">
          {readiness.egressHostCount} egress hosts
        </span>
      </div>
      <ul
        aria-label="Live input readiness"
        className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2"
      >
        {namedReadinessRows(readiness).map((row) => (
          <ReadinessItem key={row.code} row={row} />
        ))}
      </ul>
    </section>
  );
}
